import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search, Edit, Eye, Package, Trash2, Percent, PoundSterling,
  ArrowUpDown, ArrowUp, ArrowDown, Tag, ChevronLeft, ChevronRight, Download, Copy, Archive,
} from "lucide-react";
import { useAllAdminProducts, useCategories, getFirstImage, type Product } from "@/hooks/useProducts";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

type PriceMode = "set" | "discount";
type SortKey = "name" | "price" | "stock" | "published";
type SortDir = "asc" | "desc";

const PAGE_SIZE = 25;

const AdminProducts = () => {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [priceDialogOpen, setPriceDialogOpen] = useState(false);
  const [priceMode, setPriceMode] = useState<PriceMode>("set");
  const [priceValue, setPriceValue] = useState("");
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [page, setPage] = useState(0);
  const [filterCategory, setFilterCategory] = useState("");
  const [filterStock, setFilterStock] = useState<"" | "in" | "out">("");
  const [filterPublished, setFilterPublished] = useState<"" | "yes" | "no">("");
  const [filterType, setFilterType] = useState<"" | "simple" | "variable">("");
  const { data: products, isLoading } = useAllAdminProducts(search || undefined);
  const { data: categories } = useCategories();
  const qc = useQueryClient();

  // Filter → Sort → Paginate
  const filteredProducts = useMemo(() => {
    if (!products) return [];
    return products.filter((p) => {
      if (filterCategory && !(p.categories ?? "").toLowerCase().includes(filterCategory.toLowerCase())) return false;
      if (filterStock === "in" && !p.in_stock) return false;
      if (filterStock === "out" && p.in_stock) return false;
      if (filterPublished === "yes" && !p.published) return false;
      if (filterPublished === "no" && p.published) return false;
      if (filterType && p.type !== filterType) return false;
      return true;
    });
  }, [products, filterCategory, filterStock, filterPublished, filterType]);

  const sortedProducts = useMemo(() => {
    return [...filteredProducts].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case "name": cmp = a.name.localeCompare(b.name); break;
        case "price": cmp = (a.regular_price ?? 0) - (b.regular_price ?? 0); break;
        case "stock": cmp = (a.in_stock ? 1 : 0) - (b.in_stock ? 1 : 0); break;
        case "published": cmp = (a.published ? 1 : 0) - (b.published ? 1 : 0); break;
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [filteredProducts, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sortedProducts.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pagedProducts = sortedProducts.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  // Reset page when filters change
  const resetPage = () => setPage(0);

  const activeFilterCount = [filterCategory, filterStock, filterPublished, filterType].filter(Boolean).length;

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
    return sortDir === "asc" ? <ArrowUp className="w-3 h-3 ml-1" /> : <ArrowDown className="w-3 h-3 ml-1" />;
  };

  const allPageSelected = pagedProducts.length > 0 && pagedProducts.every((p) => selected.has(p.id));
  const allFilteredSelected = sortedProducts.length > 0 && selected.size === sortedProducts.length;

  const toggleAll = () => {
    if (allPageSelected) {
      setSelected((prev) => {
        const next = new Set(prev);
        pagedProducts.forEach((p) => next.delete(p.id));
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        pagedProducts.forEach((p) => next.add(p.id));
        return next;
      });
    }
  };

  const selectAllFiltered = () => setSelected(new Set(sortedProducts.map((p) => p.id)));
  const deselectAll = () => setSelected(new Set());

  const toggleOne = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const exportCsv = () => {
    const rows = sortedProducts.map((p) => ({
      Name: p.name,
      SKU: p.sku || "",
      Type: p.type,
      "Regular Price": p.regular_price?.toFixed(2) ?? "",
      "Sale Price": p.sale_price?.toFixed(2) ?? "",
      "In Stock": p.in_stock ? "Yes" : "No",
      Published: p.published ? "Yes" : "No",
      Categories: p.categories || "",
    }));
    const headers = Object.keys(rows[0] || {});
    const csvContent = [
      headers.join(","),
      ...rows.map((r) =>
        headers.map((h) => {
          const val = String((r as any)[h]);
          return val.includes(",") || val.includes('"') ? `"${val.replace(/"/g, '""')}"` : val;
        }).join(",")
      ),
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `products-export-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} product(s) to CSV`);
  };

  const duplicateProduct = async (id: number) => {
    const product = products?.find((p) => p.id === id);
    if (!product) return;
    const { id: _id, created_at, updated_at, slug, ...rest } = product;
    const { error } = await supabase.from("products").insert({ ...rest, name: `${product.name} (Copy)`, slug: null, published: false } as any);
    if (error) { toast.error("Duplicate failed"); return; }
    invalidateAll();
    toast.success(`Duplicated "${product.name}"`);
    await supabase.from("admin_activity_log").insert({ user_email: (await supabase.auth.getUser()).data.user?.email || "unknown", action: "duplicated_product", entity_type: "product", entity_id: String(id) });
  };

  const archiveProduct = async (id: number) => {
    const { error } = await supabase.from("products").update({ published: false, in_stock: false }).eq("id", id);
    if (error) { toast.error("Archive failed"); return; }
    invalidateAll();
    toast.success("Product archived (unpublished & out of stock)");
    await supabase.from("admin_activity_log").insert({ user_email: (await supabase.auth.getUser()).data.user?.email || "unknown", action: "archived_product", entity_type: "product", entity_id: String(id) });
  };

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ["products"] });
    qc.invalidateQueries({ queryKey: ["admin-products"] });
    qc.invalidateQueries({ queryKey: ["categories"] });
  };

  const bulkUpdate = async (updates: Partial<Product>) => {
    setBulkLoading(true);
    try {
      const ids = [...selected];
      const { error } = await supabase.from("products").update(updates).in("id", ids);
      if (error) throw error;
      invalidateAll();
      toast.success(`Updated ${ids.length} product(s)`);
      setSelected(new Set());
    } catch (e: any) {
      toast.error(e.message || "Bulk update failed");
    } finally { setBulkLoading(false); }
  };

  const bulkDelete = async () => {
    if (!confirm(`Delete ${selected.size} product(s)? This cannot be undone.`)) return;
    setBulkLoading(true);
    try {
      const ids = [...selected];
      const { error } = await supabase.from("products").delete().in("id", ids);
      if (error) throw error;
      invalidateAll();
      toast.success(`Deleted ${ids.length} product(s)`);
      setSelected(new Set());
    } catch (e: any) {
      toast.error(e.message || "Bulk delete failed");
    } finally { setBulkLoading(false); }
  };

  const handleBulkPrice = async () => {
    const val = parseFloat(priceValue);
    if (isNaN(val) || val < 0) { toast.error("Please enter a valid number"); return; }
    setBulkLoading(true);
    try {
      const ids = [...selected];
      if (priceMode === "set") {
        const { error } = await supabase.from("products").update({ regular_price: val }).in("id", ids);
        if (error) throw error;
      } else {
        const sel = products?.filter((p) => ids.includes(p.id) && p.regular_price != null) ?? [];
        for (const p of sel) {
          const sp = Math.round(p.regular_price! * (1 - val / 100) * 100) / 100;
          const { error } = await supabase.from("products").update({ sale_price: sp }).eq("id", p.id);
          if (error) throw error;
        }
      }
      invalidateAll();
      toast.success(priceMode === "set" ? `Set price to £${val.toFixed(2)} for ${ids.length} product(s)` : `Applied ${val}% discount to ${ids.length} product(s)`);
      setSelected(new Set());
      setPriceDialogOpen(false);
      setPriceValue("");
    } catch (e: any) {
      toast.error(e.message || "Price update failed");
    } finally { setBulkLoading(false); }
  };

  const handleBulkCategory = async () => {
    if (!selectedCategory) { toast.error("Please select a category"); return; }
    setBulkLoading(true);
    try {
      const ids = [...selected];
      const { error } = await supabase.from("products").update({ categories: selectedCategory }).in("id", ids);
      if (error) throw error;
      invalidateAll();
      toast.success(`Set category to "${selectedCategory}" for ${ids.length} product(s)`);
      setSelected(new Set());
      setCategoryDialogOpen(false);
      setSelectedCategory("");
    } catch (e: any) {
      toast.error(e.message || "Category update failed");
    } finally { setBulkLoading(false); }
  };

  const selectStyle = "h-9 rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold">Products</h1>
          <p className="text-sm text-muted-foreground font-body">{filteredProducts.length} of {products?.length ?? 0} products</p>
        </div>
        <Button size="sm" variant="outline" onClick={exportCsv} disabled={sortedProducts.length === 0}>
          <Download className="w-4 h-4 mr-1.5" />Export CSV
        </Button>
      </div>

      {/* Search + Filters */}
      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div className="relative max-w-md flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search by name or SKU…" value={search} onChange={(e) => { setSearch(e.target.value); resetPage(); }} className="pl-10" />
        </div>
        <select value={filterCategory} onChange={(e) => { setFilterCategory(e.target.value); resetPage(); }} className={selectStyle}>
          <option value="">All Categories</option>
          {categories?.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
        </select>
        <select value={filterStock} onChange={(e) => { setFilterStock(e.target.value as any); resetPage(); }} className={selectStyle}>
          <option value="">All Stock</option>
          <option value="in">In Stock</option>
          <option value="out">Out of Stock</option>
        </select>
        <select value={filterPublished} onChange={(e) => { setFilterPublished(e.target.value as any); resetPage(); }} className={selectStyle}>
          <option value="">All Status</option>
          <option value="yes">Published</option>
          <option value="no">Hidden</option>
        </select>
        <select value={filterType} onChange={(e) => { setFilterType(e.target.value as any); resetPage(); }} className={selectStyle}>
          <option value="">All Types</option>
          <option value="simple">Simple</option>
          <option value="variable">Variable</option>
        </select>
        {activeFilterCount > 0 && (
          <Button size="sm" variant="ghost" onClick={() => { setFilterCategory(""); setFilterStock(""); setFilterPublished(""); setFilterType(""); resetPage(); }}>
            Clear filters
          </Button>
        )}
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 mb-4 p-3 rounded-lg bg-primary/10 border border-primary/20">
          <div className="flex flex-col gap-1">
            <span className="text-sm font-body font-medium">{selected.size} selected</span>
            {allPageSelected && !allFilteredSelected && sortedProducts.length > PAGE_SIZE && (
              <button onClick={selectAllFiltered} className="text-xs text-primary hover:underline text-left font-body">
                Select all {sortedProducts.length} products across pages
              </button>
            )}
            {allFilteredSelected && sortedProducts.length > PAGE_SIZE && (
              <button onClick={deselectAll} className="text-xs text-primary hover:underline text-left font-body">
                Deselect all
              </button>
            )}
          </div>
          <div className="flex gap-2 ml-auto flex-wrap">
            <Button size="sm" variant="outline" disabled={bulkLoading} onClick={() => bulkUpdate({ published: true })}>Publish</Button>
            <Button size="sm" variant="outline" disabled={bulkLoading} onClick={() => bulkUpdate({ published: false })}>Unpublish</Button>
            <Button size="sm" variant="outline" disabled={bulkLoading} onClick={() => bulkUpdate({ in_stock: true })}>Mark In Stock</Button>
            <Button size="sm" variant="outline" disabled={bulkLoading} onClick={() => bulkUpdate({ in_stock: false })}>Mark Out of Stock</Button>
            <Button size="sm" variant="outline" disabled={bulkLoading} onClick={() => { setPriceMode("set"); setPriceValue(""); setPriceDialogOpen(true); }}>
              <PoundSterling className="w-3.5 h-3.5 mr-1" />Set Price
            </Button>
            <Button size="sm" variant="outline" disabled={bulkLoading} onClick={() => { setPriceMode("discount"); setPriceValue(""); setPriceDialogOpen(true); }}>
              <Percent className="w-3.5 h-3.5 mr-1" />Discount
            </Button>
            <Button size="sm" variant="outline" disabled={bulkLoading} onClick={() => { setSelectedCategory(""); setCategoryDialogOpen(true); }}>
              <Tag className="w-3.5 h-3.5 mr-1" />Category
            </Button>
            <Button size="sm" variant="outline" disabled={bulkLoading} onClick={() => bulkUpdate({ sale_price: null })}>
              Clear Sale Price
            </Button>
            <Button size="sm" variant="destructive" disabled={bulkLoading} onClick={bulkDelete}>
              <Trash2 className="w-3.5 h-3.5 mr-1" />Delete
            </Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="text-muted-foreground text-center py-20">Loading…</div>
      ) : (
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-secondary/50">
                  <th className="px-4 py-3 w-10">
                    <Checkbox checked={allPageSelected} onCheckedChange={toggleAll} aria-label="Select all on page" />
                  </th>
                  <th className="text-left px-4 py-3 font-body font-medium text-muted-foreground">Image</th>
                  <th className="text-left px-4 py-3 font-body font-medium text-muted-foreground cursor-pointer select-none" onClick={() => toggleSort("name")}>
                    <span className="inline-flex items-center">Name<SortIcon col="name" /></span>
                  </th>
                  <th className="text-left px-4 py-3 font-body font-medium text-muted-foreground hidden md:table-cell">SKU</th>
                  <th className="text-left px-4 py-3 font-body font-medium text-muted-foreground hidden md:table-cell">Type</th>
                  <th className="text-left px-4 py-3 font-body font-medium text-muted-foreground cursor-pointer select-none" onClick={() => toggleSort("price")}>
                    <span className="inline-flex items-center">Price<SortIcon col="price" /></span>
                  </th>
                  <th className="text-left px-4 py-3 font-body font-medium text-muted-foreground cursor-pointer select-none" onClick={() => toggleSort("stock")}>
                    <span className="inline-flex items-center">Stock<SortIcon col="stock" /></span>
                  </th>
                  <th className="text-left px-4 py-3 font-body font-medium text-muted-foreground hidden sm:table-cell cursor-pointer select-none" onClick={() => toggleSort("published")}>
                    <span className="inline-flex items-center">Published<SortIcon col="published" /></span>
                  </th>
                  <th className="text-left px-4 py-3 font-body font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedProducts.map((p) => (
                  <tr key={p.id} className={`border-b border-border transition-colors ${selected.has(p.id) ? "bg-primary/5" : "hover:bg-secondary/30"}`}>
                    <td className="px-4 py-3">
                      <Checkbox checked={selected.has(p.id)} onCheckedChange={() => toggleOne(p.id)} aria-label={`Select ${p.name}`} />
                    </td>
                    <td className="px-4 py-3">
                      <img src={getFirstImage(p.images)} alt="" className="w-10 h-10 rounded object-cover bg-muted" />
                    </td>
                    <td className="px-4 py-3 font-body font-medium max-w-[200px] truncate">{p.name}</td>
                    <td className="px-4 py-3 font-body text-muted-foreground hidden md:table-cell">{p.sku || "—"}</td>
                    <td className="px-4 py-3 font-body hidden md:table-cell">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${p.type === "variable" ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"}`}>{p.type}</span>
                    </td>
                    <td className="px-4 py-3 font-body">
                      <div className="flex flex-col">
                        <span>{p.regular_price ? `£${p.regular_price.toFixed(2)}` : "—"}</span>
                        {p.sale_price != null && <span className="text-xs text-green-600">Sale: £{p.sale_price.toFixed(2)}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-body font-medium ${p.in_stock ? "text-green-600" : "text-destructive"}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${p.in_stock ? "bg-green-500" : "bg-destructive"}`} />
                        {p.in_stock ? "In stock" : "Out"}
                      </span>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-body font-medium ${p.published ? "text-green-600" : "text-muted-foreground"}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${p.published ? "bg-green-500" : "bg-muted-foreground"}`} />
                        {p.published ? "Published" : "Hidden"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <Link to={`/admin/edit/${p.id}`} className="p-1.5 rounded hover:bg-secondary transition-colors" title="Edit"><Edit className="w-4 h-4" /></Link>
                        <Link to={`/product/${p.slug}`} target="_blank" className="p-1.5 rounded hover:bg-secondary transition-colors" title="View"><Eye className="w-4 h-4" /></Link>
                        <button onClick={() => duplicateProduct(p.id)} className="p-1.5 rounded hover:bg-secondary transition-colors" title="Duplicate"><Copy className="w-4 h-4" /></button>
                        <button onClick={() => archiveProduct(p.id)} className="p-1.5 rounded hover:bg-secondary transition-colors" title="Archive (unpublish)"><Archive className="w-4 h-4 text-muted-foreground" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {sortedProducts.length === 0 && (
            <div className="text-center py-12 text-muted-foreground font-body">
              <Package className="w-10 h-10 mx-auto mb-3 opacity-40" />No products found
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border">
              <p className="text-sm text-muted-foreground font-body">
                Showing {safePage * PAGE_SIZE + 1}–{Math.min((safePage + 1) * PAGE_SIZE, sortedProducts.length)} of {sortedProducts.length}
              </p>
              <div className="flex items-center gap-1">
                <Button size="icon" variant="ghost" disabled={safePage === 0} onClick={() => setPage(safePage - 1)} className="h-8 w-8">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                {Array.from({ length: totalPages }, (_, i) => i).map((i) => {
                  // Show first, last, current ±1
                  if (i === 0 || i === totalPages - 1 || Math.abs(i - safePage) <= 1) {
                    return (
                      <Button key={i} size="sm" variant={i === safePage ? "default" : "ghost"} onClick={() => setPage(i)} className="h-8 w-8 p-0">
                        {i + 1}
                      </Button>
                    );
                  }
                  if (i === 1 && safePage > 2) return <span key="el" className="px-1 text-muted-foreground">…</span>;
                  if (i === totalPages - 2 && safePage < totalPages - 3) return <span key="er" className="px-1 text-muted-foreground">…</span>;
                  return null;
                })}
                <Button size="icon" variant="ghost" disabled={safePage >= totalPages - 1} onClick={() => setPage(safePage + 1)} className="h-8 w-8">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Bulk Price Dialog */}
      <Dialog open={priceDialogOpen} onOpenChange={setPriceDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">{priceMode === "set" ? "Set Price" : "Apply Percentage Discount"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground font-body">
              {priceMode === "set" ? `Set the regular price for ${selected.size} selected product(s).` : `Apply a percentage discount as sale price for ${selected.size} selected product(s).`}
            </p>
            <div className="space-y-2">
              <Label className="font-body">{priceMode === "set" ? "New Price (£)" : "Discount (%)"}</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">{priceMode === "set" ? "£" : "%"}</span>
                <Input type="number" min="0" step={priceMode === "set" ? "0.01" : "1"} max={priceMode === "discount" ? "100" : undefined} placeholder={priceMode === "set" ? "9.99" : "20"} value={priceValue} onChange={(e) => setPriceValue(e.target.value)} className="pl-8" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPriceDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleBulkPrice} disabled={bulkLoading || !priceValue}>{bulkLoading ? "Updating…" : "Apply"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Category Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Assign Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground font-body">Set the category for {selected.size} selected product(s).</p>
            <div className="space-y-2">
              <Label className="font-body">Category</Label>
              <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} className={`flex h-10 w-full ${selectStyle}`}>
                <option value="">Select a category…</option>
                {categories?.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCategoryDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleBulkCategory} disabled={bulkLoading || !selectedCategory}>{bulkLoading ? "Updating…" : "Apply"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminProducts;
